/**
 * Sonido corto que avisa que Bryant terminó de responder.
 *
 * Se genera con Web Audio (dos notas suaves) en vez de un archivo, así no hay
 * un asset más que cargar. Los navegadores sólo dejan sonar un AudioContext
 * creado o reanudado durante un gesto del usuario: por eso `unlock()` se llama
 * al enviar el mensaje y `play()` recién cuando termina el stream.
 */
export class DoneChime {

  private context: AudioContext | null = null;

  /**
   * Crea o reanuda el contexto de audio. Llamarlo dentro de un gesto del
   * usuario (click, Enter) para que después el navegador deje sonar el aviso.
   */
  unlock(): void {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;

      this.context = this.context ?? new AudioCtx();
      if (this.context.state === 'suspended') {
        this.context.resume().catch(() => { /* sin sonido, no es crítico */ });
      }
    } catch {
      this.context = null;
    }
  }

  /**
   * Toca el aviso: dos notas ascendentes (Mi → La) de ~0,4 s en total.
   */
  play(): void {
    const ctx = this.context;
    if (!ctx || ctx.state !== 'running') return;

    const start = ctx.currentTime;
    [659.25, 880].forEach((frequency, i) => this.note(ctx, frequency, start + i * 0.12, 0.28));
  }

  /** Una nota con ataque y caída cortos para que no haga "click" */
  private note(ctx: AudioContext, frequency: number, at: number, duration: number): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.value = frequency;

    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(0.12, at + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);

    osc.connect(gain).connect(ctx.destination);
    osc.start(at);
    osc.stop(at + duration + 0.02);
  }
}
